import { gql, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import type { Device } from '../../../model';
import * as Dbx from './dbx';

interface TData {
  getLocations?: {
    locations: Dbx.Record[];
    page: number;
    total: number;
  };
}

interface TVariables {
  input: {
    extra_filter: { only_with_encoders: boolean };
  };
}

/*
 * Fetches the FULL set of encoder-equipped locations in one call (no server
 * pagination). Stats, filtering, search, sorting and pagination are all done
 * client-side — the backend can't return per-status counts while paginating.
 */
export const QUERY = gql`
query GetLocations($input: GetLocationsInput) {
  getLocations(location: $input) {
    locations {
      name
      id
      user {
        id
        organization_name
      }
      nextEvent {
        id
        name
        date_created
        start_time
        end_time
        status
        operator_name
      }
      eventHappening
      encoder_configs {
        bad_internet_speed_upload
        bad_internet_speed_download
        ems_version
        lc_version
        splashtop_status
      }
      screenshare
      encoder_status {
        date_updated
        online_status
        internet_speed_download
        internet_speed_upload
      }
    }
    page
    total
  }
}
`;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useDevices = () => {
  const { data, loading, error, refetch } = useQuery<TData, TVariables>(QUERY, {
    variables: {
      input: {
        extra_filter: { only_with_encoders: true }
      }
    }
  });

  const devices = useMemo(
    () => data?.getLocations?.locations.map(Dbx.normalize).filter((v): v is Device => !!v),
    [data]
  );

  const refetch_ = useCallback(async () => refetch(), [refetch]);

  return { devices, loading, error, refetch: refetch_ };
};
